/**
 * V146 (IMPRESION) — Pantalla de configuración de impresoras. Registra impresoras con
 * NOMBRE + ROL (caja/cocina/barra) + BACKEND, guarda en el SERVIDOR (por tenant/sucursal),
 * y deja PROBAR cada una. Instrucciones por escenario (PC / tablet) en lenguaje claro.
 * Los fallos NUNCA son silenciosos: la prueba muestra su resultado en grande.
 */
import { useEffect, useState } from 'react';
import {
  Printer as PrinterIcon, Plus, Trash2, Play, CheckCircle2, AlertTriangle,
  RefreshCw, Pencil, X, Info,
} from 'lucide-react';
import * as qz from '../lib/qz';
import { fetchPrinters, createPrinter, updatePrinter, deletePrinter, testPrint, PrinterInput } from './config';
import { Printer, PrinterRole, PrinterBackend, ROLE_LABEL, BACKEND_LABEL } from './types';
import type { PrintOutcome } from './router';

const ROLES: PrinterRole[] = ['caja', 'cocina', 'barra'];
const BACKENDS: PrinterBackend[] = ['qz', 'network', 'browser'];

const btnPrimary = 'inline-flex items-center gap-1.5 rounded-xl bg-[var(--brand-primary)] px-3 py-2 text-xs font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[var(--brand-primary)]';
const btnGhost = 'inline-flex items-center gap-1.5 rounded-xl bg-white px-3 py-2 text-xs font-medium text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white';

const emptyForm = (branchId: string | null): PrinterInput => ({
  branchId, name: '', role: 'caja', backendKind: 'browser', paperWidth: 80,
  qzPrinterName: '', netHost: '', netPort: 8043, isActive: true, sortOrder: 0,
});

export function PrintersSettings({ branchId = null, branchName, canManage = true }:
  { branchId?: string | null; branchName?: string; canManage?: boolean }) {
  const [printers, setPrinters] = useState<Printer[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [editing, setEditing] = useState<PrinterInput & { id?: string } | null>(null);
  const [testResult, setTestResult] = useState<{ id: string; r: PrintOutcome } | null>(null);
  const [testingId, setTestingId] = useState<string | null>(null);

  const load = async () => {
    setLoading(true); setErr(null);
    try { setPrinters(await fetchPrinters(branchId)); }
    catch (e: any) { setErr(e?.body?.message || e?.message || 'No se pudieron cargar las impresoras'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [branchId]);

  const save = async () => {
    if (!editing) return;
    setErr(null);
    try {
      const body: PrinterInput = { ...editing, branchId };
      if (editing.id) await updatePrinter(editing.id, body);
      else await createPrinter(body);
      setEditing(null);
      await load();
    } catch (e: any) { setErr(e?.body?.message || e?.message || 'No se pudo guardar la impresora'); }
  };

  const remove = async (p: Printer) => {
    if (!window.confirm(`¿Eliminar la impresora "${p.name}"?`)) return;
    setErr(null);
    try { await deletePrinter(p.id); await load(); }
    catch (e: any) { setErr(e?.body?.message || e?.message || 'No se pudo eliminar'); }
  };

  const runTest = async (p: Printer) => {
    setTestingId(p.id); setTestResult(null);
    const r = await testPrint(p);
    setTestResult({ id: p.id, r });
    setTestingId(null);
  };

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <header>
        <h2 className="flex items-center gap-2 font-serif text-2xl tracking-tight text-slate-900">
          <PrinterIcon className="h-6 w-6 text-slate-400" /> Impresoras
        </h2>
        <p className="mt-1 text-sm text-slate-500">
          Configura una vez y queda guardado en el servidor{branchName ? ` para ${branchName}` : ''} — al cambiar de
          equipo NO tienes que reconfigurar. Cada documento se imprime según el rol:
          <span className="font-medium text-slate-700"> ticket de venta → Caja, comanda → Cocina, corte → Caja.</span>
        </p>
      </header>

      <ScenarioHelp />

      {err && (
        <div className="rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-700 ring-1 ring-rose-200">{err}</div>
      )}

      {/* Lista */}
      <div className="space-y-2">
        {loading ? (
          <div className="h-16 animate-pulse rounded-xl bg-slate-100" />
        ) : printers.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500">
            Aún no hay impresoras. Agrega la primera para empezar a imprimir tickets.
          </div>
        ) : (
          printers.map((p) => (
            <div key={p.id} className="rounded-xl border border-slate-200 bg-white p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-medium text-slate-800">
                    {p.name}
                    {!p.isActive && <span className="ml-2 text-[11px] text-slate-400">(inactiva)</span>}
                    {!p.branchId && <span className="ml-2 text-[11px] text-slate-400">· todo el tenant</span>}
                  </p>
                  <p className="text-xs text-slate-500">
                    <span className="rounded bg-slate-100 px-1.5 py-0.5 font-medium text-slate-600">{ROLE_LABEL[p.role]}</span>
                    {' · '}{BACKEND_LABEL[p.backendKind]}{' · '}{p.paperWidth}mm
                    {p.backendKind === 'qz' && p.qzPrinterName ? ` · ${p.qzPrinterName}` : ''}
                    {p.backendKind === 'network' && p.netHost ? ` · ${p.netHost}:${p.netPort ?? 8043}` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-1.5">
                  <button className={btnGhost} onClick={() => runTest(p)} disabled={testingId === p.id}>
                    {testingId === p.id ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
                    Prueba
                  </button>
                  {canManage && (
                    <>
                      <button className={btnGhost} onClick={() => setEditing({ ...toInput(p), id: p.id })}><Pencil className="h-3.5 w-3.5" /></button>
                      <button className={btnGhost} onClick={() => remove(p)}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></button>
                    </>
                  )}
                </div>
              </div>
              {testResult?.id === p.id && <TestResultBanner r={testResult.r} onClose={() => setTestResult(null)} />}
            </div>
          ))
        )}
      </div>

      {canManage && !editing && (
        <button className={btnPrimary} onClick={() => setEditing(emptyForm(branchId))}>
          <Plus className="h-4 w-4" /> Agregar impresora
        </button>
      )}

      {editing && (
        <PrinterForm
          value={editing}
          onChange={setEditing}
          onCancel={() => setEditing(null)}
          onSave={save}
        />
      )}
    </div>
  );
}

function toInput(p: Printer): PrinterInput {
  return {
    branchId: p.branchId, name: p.name, role: p.role, backendKind: p.backendKind,
    paperWidth: p.paperWidth, qzPrinterName: p.qzPrinterName ?? '', netHost: p.netHost ?? '',
    netPort: p.netPort ?? 8043, isActive: p.isActive, sortOrder: p.sortOrder,
  };
}

// ---------------------------------------------------------------------------
function PrinterForm({ value, onChange, onCancel, onSave }: {
  value: PrinterInput & { id?: string };
  onChange: (v: PrinterInput & { id?: string }) => void;
  onCancel: () => void;
  onSave: () => void;
}) {
  const [qzList, setQzList] = useState<string[]>([]);
  const [qzMsg, setQzMsg] = useState<string | null>(null);
  const set = (patch: Partial<PrinterInput>) => onChange({ ...value, ...patch });

  const detectQz = async () => {
    setQzMsg('Buscando…');
    try { const l = await qz.listPrinters(); setQzList(l); setQzMsg(l.length ? null : 'QZ Tray conectado, sin impresoras instaladas.'); }
    catch (e: any) { setQzMsg(e?.message || 'No se pudo conectar con QZ Tray.'); }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="font-medium text-slate-800">{value.id ? 'Editar impresora' : 'Nueva impresora'}</h3>
        <button onClick={onCancel} className="text-slate-400 hover:text-slate-700"><X className="h-4 w-4" /></button>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="text-sm">
          <span className="text-xs font-medium text-slate-600">Nombre</span>
          <input className="input mt-1" value={value.name} onChange={(e) => set({ name: e.target.value })} placeholder="Caja principal" />
        </label>
        <label className="text-sm">
          <span className="text-xs font-medium text-slate-600">Rol (a dónde va cada documento)</span>
          <select className="input mt-1" value={value.role} onChange={(e) => set({ role: e.target.value as PrinterRole })}>
            {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
          </select>
        </label>
        <label className="text-sm">
          <span className="text-xs font-medium text-slate-600">Cómo imprime</span>
          <select className="input mt-1" value={value.backendKind} onChange={(e) => set({ backendKind: e.target.value as PrinterBackend })}>
            {BACKENDS.map((b) => <option key={b} value={b}>{BACKEND_LABEL[b]}</option>)}
          </select>
        </label>
        <label className="text-sm">
          <span className="text-xs font-medium text-slate-600">Ancho de papel</span>
          <select className="input mt-1" value={value.paperWidth} onChange={(e) => set({ paperWidth: Number(e.target.value) })}>
            <option value={80}>80 mm</option>
            <option value={58}>58 mm</option>
          </select>
        </label>

        {value.backendKind === 'qz' && (
          <div className="sm:col-span-2">
            <span className="text-xs font-medium text-slate-600">Impresora en QZ Tray</span>
            <div className="mt-1 flex items-center gap-2">
              {qzList.length ? (
                <select className="input flex-1" value={value.qzPrinterName ?? ''} onChange={(e) => set({ qzPrinterName: e.target.value })}>
                  <option value="">— Elegir —</option>
                  {qzList.map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              ) : (
                <input className="input flex-1" value={value.qzPrinterName ?? ''} onChange={(e) => set({ qzPrinterName: e.target.value })} placeholder="Nombre exacto en el sistema" />
              )}
              <button className={btnGhost} onClick={detectQz} type="button">Detectar</button>
            </div>
            {qzMsg && <p className="mt-1 text-[11px] text-slate-500">{qzMsg}</p>}
          </div>
        )}

        {value.backendKind === 'network' && (
          <>
            <label className="text-sm">
              <span className="text-xs font-medium text-slate-600">IP / host de la impresora</span>
              <input className="input mt-1" value={value.netHost ?? ''} onChange={(e) => set({ netHost: e.target.value })} placeholder="192.168.1.50" />
            </label>
            <label className="text-sm">
              <span className="text-xs font-medium text-slate-600">Puerto</span>
              <input className="input mt-1" type="number" value={value.netPort ?? 8043} onChange={(e) => set({ netPort: Number(e.target.value) })} />
            </label>
          </>
        )}

        <label className="flex items-center gap-2 text-sm sm:col-span-2">
          <input type="checkbox" checked={value.isActive ?? true} onChange={(e) => set({ isActive: e.target.checked })} />
          <span className="text-slate-700">Activa</span>
        </label>
      </div>

      <div className="mt-4 flex justify-end gap-2">
        <button className={btnGhost} onClick={onCancel}>Cancelar</button>
        <button className={btnPrimary} onClick={onSave} disabled={!value.name.trim()}>Guardar</button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function TestResultBanner({ r, onClose }: { r: PrintOutcome; onClose: () => void }) {
  const good = r.ok && !r.usedFallback && r.method !== 'browser';
  const warn = r.method === 'browser' && r.ok;
  const cls = good
    ? 'bg-emerald-50 text-emerald-800 ring-emerald-200'
    : warn
      ? 'bg-amber-50 text-amber-800 ring-amber-200'
      : 'bg-rose-50 text-rose-800 ring-rose-200';
  return (
    <div className={`mt-2 flex items-start gap-2 rounded-xl px-3 py-2 text-xs ring-1 ${cls}`}>
      {good ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" /> : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />}
      <div className="flex-1">
        {good && `Enviado a "${r.printerName}" (${r.method}). Revisa que haya salido el papel.`}
        {warn && `No hubo impresora silenciosa: se abrió la vista del navegador para imprimir. ${r.error ?? ''}`}
        {!r.ok && `No se pudo imprimir. ${r.error ?? ''}`}
      </div>
      <button onClick={onClose} className="opacity-60 hover:opacity-100"><X className="h-3.5 w-3.5" /></button>
    </div>
  );
}

// ---------------------------------------------------------------------------
function ScenarioHelp() {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/60">
      <button onClick={() => setOpen(!open)} className="flex w-full items-center gap-2 px-4 py-3 text-left text-sm font-medium text-slate-700">
        <Info className="h-4 w-4 text-slate-400" /> ¿Cómo conecto mi impresora? {open ? '▲' : '▼'}
      </button>
      {open && (
        <div className="space-y-3 px-4 pb-4 text-sm text-slate-600">
          <div>
            <p className="font-medium text-slate-800">Tengo una computadora (PC/Mac) con la impresora por USB</p>
            <p>Instala <span className="font-medium">QZ Tray</span> en esa computadora (una vez). Agrega la impresora aquí con
              “Cómo imprime = QZ Tray”, presiona <span className="font-medium">Detectar</span> y elígela. Imprime sin diálogo.</p>
          </div>
          <div>
            <p className="font-medium text-slate-800">Tengo una tablet o iPad</p>
            <p>Si tu impresora es de <span className="font-medium">red Epson (ePOS)</span>, agrega su IP con “Cómo imprime = Red / ePOS”:
              imprime sola, sin diálogo, en iPad y Android. Si no, usa “Navegador”: imprime con el diálogo del sistema
              (AirPrint en iPad, servicio de impresión en Android) — funciona con casi cualquier impresora.</p>
          </div>
          <div>
            <p className="font-medium text-slate-800">No estoy seguro / quiero probar rápido</p>
            <p>Empieza con “Cómo imprime = Navegador”. Funciona en todo con un diálogo. Cuando tengas el equipo definitivo,
              cámbiala a QZ Tray o Red para que imprima sola.</p>
          </div>
        </div>
      )}
    </div>
  );
}
